// Office Dialog API wrapper for Microsoft Azure SSO via Supabase.
//
// Flow:
//   1. Taskpane calls openSSODialog() → opens auth.html in an Office dialog.
//   2. auth.html (same origin, localStorage shared) kicks off
//      supabase.auth.signInWithOAuth({ provider: 'azure', redirectTo: auth.html }).
//      The dialog window itself redirects to Microsoft → back to auth.html?code=…
//   3. auth.html exchanges the code via PKCE (verifier was stored in shared localStorage)
//      and messageParent's the session JSON back to the taskpane.
//   4. Taskpane calls supabase.auth.setSession(...) → onAuthStateChange fires.

import { supabase } from "./supabase";

interface DialogMessage {
  ok: boolean;
  access_token?: string;
  refresh_token?: string;
  error?: string;
}

export function openSSODialog(): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/auth.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 30, promptBeforeOpen: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message || "Dialog open failed"));
          return;
        }
        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          try {
            const payload = JSON.parse((arg as { message: string }).message) as DialogMessage;
            dialog.close();
            if (!payload.ok || !payload.access_token || !payload.refresh_token) {
              reject(new Error(payload.error || "SSO failed"));
              return;
            }
            const { error } = await supabase.auth.setSession({
              access_token: payload.access_token,
              refresh_token: payload.refresh_token,
            });
            if (error) reject(error);
            else resolve();
          } catch (e) {
            dialog.close();
            reject(e);
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          // User closed the dialog manually
          reject(new Error("SSO geannuleerd"));
        });
      },
    );
  });
}
